import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http'
import { catchError, shareReplay, switchMap } from "rxjs/operators";
import { Router } from '@angular/router';
import { from, Observable, Subject } from 'rxjs';

import { environment } from '../../../environments/environment';
import { EncryptionService } from './encryption.service';
import { ConfigService } from './configservice.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  isAuthenticated: boolean = false;
  private token!: any;
  private userId!: any;
  private branch!: any;
  private effectiveDesignationId!: any;
  private tokenTimer!: any;
  private authStatusListener = new Subject<boolean>();
  // Emits when login-related localStorage values are saved/updated
  public loginDataUpdated: Subject<void> = new Subject<void>();
  private dynamicKrakendUrl: string | null = null; // Holds the dynamic `krakend` URL

  constructor(private http: HttpClient, private router: Router, private encryptionService: EncryptionService, private configService: ConfigService) { }

  getToken(){
    return this.token;
  }

  getUserId(){
    return this.userId;
  }

  getBranch(){
    return this.branch;
  }

  getIsAuth() {
    return this.isAuthenticated;
  }

  getPasswordChangedOn(): string | null {
    return localStorage.getItem('password_changed_on');
  }

  getIsFirstLogin(): string | null {
    return localStorage.getItem('is_first_login');
  }

  getEmail(): string | null {
    return localStorage.getItem('email');
  }

  getAuthStatusListener() {
    return this.authStatusListener.asObservable();
  }

  getKrakendUrl(): string {
    const krakendUrl = this.configService.getKrakendUrl();
    if (!krakendUrl) {
      throw new Error("Krakend URL is not available.");
    }
    return krakendUrl;
  }

  login(user: any) {
    let email = user.email;
    let password = user.password;
    return this.http.post('/api/login', {email, password}).pipe(shareReplay())
  }

  postUser(data : any) {
    const url = this.getKrakendUrl()
    if (url) {
      return this.http.post<{token: string, userid: number}>(url + '/login', data);
    } else {
        return this.http.post<{token: string, userid: number}>(environment.apiUrl + '/login', data);
    }
  }

  setLoginData(data: any) {
      const token = data.token; 
      const userId = data.userid;
      const effectiveDesignationId = data.effective_designation_id || data.effectiveDesignationId || null;
      const expiresInDuration = data.expiresIn;
      this.setAuthTimer(expiresInDuration);
      this.token = token;
      this.userId = userId;
      this.effectiveDesignationId = effectiveDesignationId;
      if (effectiveDesignationId !== null && effectiveDesignationId !== undefined) {
        localStorage.setItem('effective_designation_id', String(effectiveDesignationId));
      }
      const now = new Date();
      const expirationDate = new Date(now.getTime() + expiresInDuration * 1000);
      this.saveAuthData(token, data.userid, expirationDate);

      // Store password_changed_on and is_first_login in localStorage if they exist
      if (data.password_changed_on) {
        // store as ISO string if it's a Date, otherwise store the raw value as string
        const pwdChanged = (data.password_changed_on instanceof Date) ? data.password_changed_on.toISOString() : String(data.password_changed_on);
        localStorage.setItem('password_changed_on', pwdChanged);
      }
      if (data.is_first_login !== undefined && data.is_first_login !== null) {
        // normalize value to string '1'/'0' or 'true'/'false'
        localStorage.setItem('is_first_login', String(data.is_first_login));
      }
      if (data.email) {
        localStorage.setItem('email', String(data.email));
      }

      // Notify subscribers (components) that login-related data changed
      try {
        this.loginDataUpdated.next();
      } catch (e) {
        // noop if no subscribers
      }
  }

  setUserBranchData(data: any) {
    this.branch = data;
    localStorage.setItem('branch', data);
    this.setAuthenticationStatus();
  }

  updateUserBranchData(data: any) {
    this.branch = data;
    localStorage.setItem('branch', data);
  }

  setAuthenticationStatus() {
    if(this.token && this.userId && this.branch) {
      this.isAuthenticated = true;
      this.authStatusListener.next(true);
      this.router.navigate(['/dashboard']);
    } else {
      this.isAuthenticated = false;
    }
  } 

  autoAuthUser() {
    const authInfo = this.getAuthData();
    if(!authInfo) {
      return;
    }
    const now = new Date();
    const expiresIn = authInfo.expirationDate.getTime() - now.getTime();
    if(expiresIn > 0) {
      this.token = authInfo?.token;
      this.userId = authInfo?.userid;
      this.branch = authInfo?.branch;
      if(this.token && this.userId && this.branch) {
        this.isAuthenticated = true;
        this.setAuthTimer(expiresIn / 1000);
        this.authStatusListener.next(true);
      } else {
        this.isAuthenticated = false;
      }
    }
  }

  logout() {
    this.token = null;
    this.userId = null;
    this.branch = null;
    this.effectiveDesignationId = null;
    this.isAuthenticated = false;
    this.authStatusListener.next(false);
    this.clearAuthData();
    clearTimeout(this.tokenTimer);
    this.router.navigate(['/']);
  }

  private setAuthTimer(duration: number) {
    this. tokenTimer = setTimeout(() => {
      this.logout();
    }, duration * 1000);
  }

  private saveAuthData(token: string, userid: any, expirationDate: Date) {
    localStorage.setItem('token', token);
    localStorage.setItem('userid', userid);
    localStorage.setItem('expiration', expirationDate.toISOString());
  }

  private clearAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('userid');
    localStorage.removeItem('branch');
    localStorage.removeItem('expiration');
    localStorage.removeItem('username');
    localStorage.removeItem('password_changed_on');
    localStorage.removeItem('is_first_login');
    localStorage.removeItem('email');
    localStorage.removeItem('effective_designation_id');
  }

  private getAuthData() {
    const token = localStorage.getItem('token');
    const userid = localStorage.getItem('userid');
    const branch = localStorage.getItem('branch');
    const expirationDate = localStorage.getItem('expiration');
    if(!token || !userid || !branch || !expirationDate) {
      return;
    }
    return {
      token: token,
      userid: userid,
      branch: branch, 
      expirationDate: new Date(expirationDate)
    }
  }

  getUserMenu(data?: any): Observable<any>  {
    const url = this.getKrakendUrl()
    if (url) {
      // Encrypt URL parameters
      return from(this.encryptionService.encryptParameters(data || {})).pipe(
        switchMap(encryptedParams => {
          // Convert encrypted parameters to HttpParams
          let params = new HttpParams();
          for (const [key, value] of Object.entries(encryptedParams)) {
            params = params.set(key, value);
          }
          
          // Make the HTTP GET request with encrypted parameters
          return this.http.get<any>(url + '/user/menu', { params });
        }),
        catchError(error => {
          console.error('Error encrypting parameters or making request:', error);
          throw error;
        })
      );
    } else {
      return this.http.get<any>(environment.apiUrl + '/user/menu', { params: data });
    }
  }

}
 